import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
};

// Users API (Admin)
export const usersAPI = {
  getAll: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  delete: (id) => api.delete(`/users/${id}`),
  updateRole: (id, role) => api.put(`/users/${id}/role`, null, { params: { role } }),
  setMaster: (id, isMaster) => api.put(`/users/${id}/master`, null, { params: { is_master: isMaster } }),
};

// Employees API
export const employeesAPI = {
  getAll: (params) => api.get('/employees', { params }),
  getById: (id) => api.get(`/employees/${id}`),
  create: (data) => api.post('/employees', data),
  update: (id, data) => api.put(`/employees/${id}`, data),
  delete: (id) => api.delete(`/employees/${id}`),
  terminate: (id, data) => api.post(`/employees/${id}/terminate`, data),
  getTerminationPdf: (id) => api.get(`/employees/${id}/termination-pdf`, { responseType: 'blob' }),
  getAuditLog: (id) => api.get(`/employees/${id}/audit-log`),
  updateLeaveBalance: (id, data) => api.put(`/employees/${id}/leave-balance`, data),
  uploadDocument: (id, formData) => api.post(`/employees/${id}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  uploadPhoto: (id, formData) => api.post(`/employees/${id}/photo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  // Contract management
  renewContract: (id, data) => api.post(`/employees/${id}/contract/renew`, data),
  endContract: (id, reason) => api.post(`/employees/${id}/contract/end`, null, { params: { reason } }),
  uploadContractDocument: (id, formData) => api.post(`/employees/${id}/contract/document`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getContractHistory: (id) => api.get(`/employees/${id}/contract/history`),
};

// Contracts API
export const contractsAPI = {
  getExpiring: (days) => api.get('/contracts/expiring', { params: { days } }),
  getExpired: () => api.get('/contracts/expired'),
  getStats: () => api.get('/contracts/stats'),
  updateStatuses: () => api.post('/contracts/update-statuses'),
  getReportByType: (params) => api.get('/contracts/reports/by-type', { params }),
  getReportByTypeExcel: () => api.get('/contracts/reports/by-type', { params: { format: 'excel' }, responseType: 'blob' }),
};

// Disciplinary API
export const disciplinaryAPI = {
  getAll: (params) => api.get('/disciplinary', { params }),
  getById: (id) => api.get(`/disciplinary/${id}`),
  create: (data) => api.post('/disciplinary', data),
  update: (id, data) => api.put(`/disciplinary/${id}`, data),
  uploadEvidence: (id, formData) => api.post(`/disciplinary/${id}/evidence`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

// Leave API
export const leaveAPI = {
  getAll: (params) => api.get('/leave', { params }),
  getCalendar: (params) => api.get('/leave/calendar', { params }),
  create: (data) => api.post('/leave', data),
  update: (id, data) => api.put(`/leave/${id}`, data),
  edit: (id, data) => api.put(`/leave/${id}/edit`, data),
  runAccrual: (params) => api.post('/leave/run-accrual', null, { params }),
  getAccruals: (params) => api.get('/leave/accruals', { params }),
};

// Shift Templates & Assignments API
export const shiftAPI = {
  getTemplates: () => api.get('/shift-templates'),
  createTemplate: (data) => api.post('/shift-templates', data),
  updateTemplate: (id, data) => api.put(`/shift-templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/shift-templates/${id}`),
  getAssignments: (params) => api.get('/shift-assignments', { params }),
  createAssignment: (data) => api.post('/shift-assignments', data),
  updateAssignment: (id, data) => api.put(`/shift-assignments/${id}`, data),
  deleteAssignment: (id) => api.delete(`/shift-assignments/${id}`),
  getCurrentShift: (employeeId, params) => api.get(`/employees/${employeeId}/current-shift`, { params }),
};

// Notifications API
export const notificationsAPI = {
  getAll: () => api.get('/notifications'),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  checkNotifications: () => api.post('/notifications/check'),
};

// Reports API
export const reportsAPI = {
  getEmployees: (params) => api.get('/reports/employees', { params }),
  getEmployeesExcel: (params) => api.get('/reports/employees', { params: { ...params, format: 'excel' }, responseType: 'blob' }),
  getEmployeesPdf: (params) => api.get('/reports/employees', { params: { ...params, format: 'pdf' }, responseType: 'blob' }),
  getLeaveSummary: (params) => api.get('/reports/leave-summary', { params }),
  getLeaveSummaryExcel: (params) => api.get('/reports/leave-summary', { params: { ...params, format: 'excel' }, responseType: 'blob' }),
  getDisciplinarySummary: (params) => api.get('/reports/disciplinary-summary', { params }),
  getTurnover: () => api.get('/reports/turnover'),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
};

// Departments API
export const departmentsAPI = {
  getAll: () => api.get('/departments'),
  create: (name) => api.post('/departments', null, { params: { name } }),
};

// Seed API
export const seedAPI = {
  seed: () => api.post('/seed'),
};

// PPE Items API (Master List)
export const ppeItemsAPI = {
  getAll: (params) => api.get('/ppe/items', { params }),
  getById: (id) => api.get(`/ppe/items/${id}`),
  create: (data) => api.post('/ppe/items', data),
  update: (id, data) => api.put(`/ppe/items/${id}`, data),
  delete: (id) => api.delete(`/ppe/items/${id}`),
  getCategories: () => api.get('/ppe/categories'),
};

// PPE Issues API
export const ppeIssuesAPI = {
  getAll: (params) => api.get('/ppe/issues', { params }),
  getById: (id) => api.get(`/ppe/issues/${id}`),
  getByEmployee: (employeeId) => api.get(`/ppe/issues/employee/${employeeId}`),
  create: (data) => api.post('/ppe/issues', data),
  update: (id, data) => api.put(`/ppe/issues/${id}`, data),
  void: (id, data) => api.post(`/ppe/issues/${id}/void`, data),
  getAudit: (id) => api.get(`/ppe/issues/${id}/audit`),
  getStats: () => api.get('/ppe/stats'),
  getOverdue: () => api.get('/ppe/reports/overdue'),
  getDueSoon: (days) => api.get('/ppe/reports/due-soon', { params: { days } }),
  getReportByEmployee: (params) => api.get('/ppe/reports/by-employee', { params }),
  getReportByEmployeeExcel: (params) => api.get('/ppe/reports/by-employee', { params: { ...params, format: 'excel' }, responseType: 'blob' }),
  getReportByDepartment: (params) => api.get('/ppe/reports/by-department', { params }),
  getReportByDepartmentExcel: () => api.get('/ppe/reports/by-department', { params: { format: 'excel' }, responseType: 'blob' }),
  getReportByReason: () => api.get('/ppe/reports/by-reason'),
};

// Attendance API (Time & Attendance Kiosk)
export const attendanceAPI = {
  // Kiosk endpoints
  getEmployeesForKiosk: () => api.get('/attendance/employees-for-kiosk'),
  verifyPin: (data) => api.post('/attendance/verify-pin', data),
  clock: (data) => api.post('/attendance/clock', data),
  
  // Admin endpoints
  getRecords: (month, year, employeeId, department) => 
    api.get('/attendance/records', { params: { month, year, employee_id: employeeId, department } }),
  getDailySummary: (month, year, employeeId, hourlyOnly = true, payCycleType = null, periodStart = null, periodEnd = null) => 
    api.get('/attendance/daily-summary', { params: { month, year, employee_id: employeeId, hourly_only: hourlyOnly, pay_cycle_type: payCycleType, period_start: periodStart, period_end: periodEnd } }),
  getMonthlySummary: (month, year, hourlyOnly = true, payCycleType = null, periodStart = null, periodEnd = null) => 
    api.get('/attendance/monthly-summary', { params: { month, year, hourly_only: hourlyOnly, pay_cycle_type: payCycleType, period_start: periodStart, period_end: periodEnd } }),
  getExceptions: (month, year, hourlyOnly = true, payCycleType = null, status = 'active') =>
    api.get('/attendance/exceptions', { params: { month, year, hourly_only: hourlyOnly, pay_cycle_type: payCycleType, status } }),
  exceptionsAction: (data) => api.post('/attendance/exceptions/action', data),
  exceptionsConvertToLeave: (data) => api.post('/attendance/exceptions/convert-to-leave', data),
  correct: (data) => api.post('/attendance/correct', data),
  getSeasonConfig: () => api.get('/attendance/season-config'),
  updateSeasonConfig: (data) => api.put('/attendance/season-config', data),
  exportExcel: (month, year, hourlyOnly = true, payCycleType = null, periodStart = null, periodEnd = null) => 
    api.get('/attendance/export/excel', { params: { month, year, hourly_only: hourlyOnly, pay_cycle_type: payCycleType, period_start: periodStart, period_end: periodEnd }, responseType: 'blob' }),

  // Individual employee report (from Employee Profile → T&A History)
  getEmployeeReport: (employeeId, periodStart, periodEnd) =>
    api.get('/attendance/employee-report', { params: { employee_id: employeeId, period_start: periodStart, period_end: periodEnd } }),
  exportEmployeeReport: (employeeId, periodStart, periodEnd) =>
    api.get('/attendance/employee-report/export', { params: { employee_id: employeeId, period_start: periodStart, period_end: periodEnd }, responseType: 'blob' }),
  
  // Pay periods
  getPayPeriods: (payCycleType, year) => 
    api.get('/attendance/pay-periods', { params: { pay_cycle_type: payCycleType, year } }),
  
  // Public holidays
  getPublicHolidays: () => api.get('/attendance/public-holidays'),
  addPublicHoliday: (data) => api.post('/attendance/public-holidays', data),
  deletePublicHoliday: (date) => api.delete(`/attendance/public-holidays/${date}`),
};

// Employee Notebook API (Master User only)
export const notebookAPI = {
  getEmployees: () => api.get('/notebook/employees'),
  getCategories: () => api.get('/notebook/categories'),
  getNotes: (employeeId) => api.get(`/notebook/notes/${employeeId}`),
  addNote: (data) => api.post('/notebook/notes', data),
  editNote: (noteId, data) => api.put(`/notebook/notes/${noteId}`, data),
  getAudit: (noteId) => api.get(`/notebook/notes/${noteId}/audit`),
  deleteNote: (noteId) => api.delete(`/notebook/notes/${noteId}`),
  exportPdf: (employeeId) => api.get(`/notebook/notes/${employeeId}/export`, { responseType: 'blob' }),
};

// Self-Service (Kiosk-gated) API - accessed from the kiosk after PIN verification
export const selfServiceAPI = {
  getProfile: (employee_id, pin) => api.post('/self-service/profile', { employee_id, pin }),
  getLeaveHistory: (employee_id, pin) => api.post('/self-service/leave/history', { employee_id, pin }),
  submitLeave: (payload) => api.post('/self-service/leave/submit', payload),
  getPpe: (employee_id, pin) => api.post('/self-service/ppe', { employee_id, pin }),
  getAttendance: (employee_id, pin) => api.post('/self-service/attendance', { employee_id, pin }),
};

export default api;
